<template>
	<div class="session-list-item__meta">
		<div class="session-list-item__meta__stream_title ui-title">{{ streamTitle }}</div>
		<div class="session-list-item__meta__header">
			<ui-button
				v-if="!session.uri.includes('@web.rec')"
				:class="{ active: isFavoriteReady }"
				:disabled="!isFavoriteReady"
				:icon="`${session.contact.favorite ? 'fa' : 'fal' } fa-star fa-fw`"
				@click="$emit('toggleFavorite', session)"
			/>
			<div class="session-list-item__meta__header__title">
				<div
					v-if="session.name || session.contact.name"
					class="session-list-item__meta__header__title__name ui-subheading"
				>
					{{ session.name ? session.name : session.contact.name }}
				</div>
				<div
					:class="(session.name || session.contact.name) ? 'ui-caption' : 'ui-subheading'"
					:style="[(!session.name && !session.contact.name) ? {lineHeight: '40px'} : {}]"
					class="session-list-item__meta__header__title__uri"
				>
					{{ session.uri }}
				</div>
			</div>
		</div>
		<div class="session-list-item__meta__content">
			<div
				v-if="session.established === 0"
				class="session-list-item__meta__content__connecting"
			>
				<div class="ui-title">Connecting...</div>
			</div>
			<dynamic-duration
				v-if="session.established === 1"
				:duration="session.duration"
				class="ui-display-2"
			/>
			<ui-button
				icon="fas fa-square"
				large
				raised
				secondary
				@click="$emit('stop', session)"
			/>
		</div>
		<ui-progress
			v-if="session.established === 0"
			:stroke="2"
			type="bar"
		/>
		<div class="session-list-item__meta__footer">
			<div v-if="session.stream_id" class="ui-subheading">Stream input</div>
			<div class="session-list-item__meta__footer__stream_input">
				<div>Bandwidth</div>
				<div>{{ session.bw }} kbit/s</div>
				<div>Audio packet loss</div>
				<div>{{ session.audio.loss }}%</div>
				<div>Video packet loss</div>
				<div>{{ session.video.loss }}%</div>
			</div>
			<div v-if="session.stream_id" class="ui-subheading">Stream output</div>
			<div v-if="session.stream_id" class="session-list-item__meta__footer__stream_output">
				<div>Current viewers</div>
				<div>{{ streamViewers }}</div>
			</div>
			<ui-clipboard-input
				v-if="session.stream_id"
				v-model="link"
				class="session-list-item__meta__footer__link"
				hint="Link copied to clipboard"
				label="Live stream link"
			/>
		</div>
	</div>
</template>

<script>
import DynamicDuration from "session/ui/DynamicDuration";
import UiButton from "lib/components/ui/Button";
import UiClipboardInput from "lib/components/ui/ClipboardInput";
import UiProgress from "lib/components/ui/Progress";

import alert from "lib/modules/Alert";
import http from "lib/modules/Http";

export default {
	name: "SessionItemStreamStatistics",
	components: {
		DynamicDuration,
		UiButton,
		UiClipboardInput,
		UiProgress
	},
	props: {
		session: {
			type: Object,
			required: true
		},
		streamTitle: {
			type: String,
			required: true
		}
	},
	data() {
		return {
			link: `${location.origin}/live/${this.session.stream_id}`,
			streamViewers: 0
		};
	},
	computed: {
		isFavoriteReady() {
			return typeof this.session.contact.favorite !== "undefined";
		}
	},
	created() {
		if (!this.session.stream_id)
			return;

		this.streamViewersUpdateInterval = setInterval(() => {
			http.get(`/api/v2/recorder/stream/${this.session.stream_id}/status`)
			.then((response) => {
				this.streamViewers = response.data.viewers;
			})
			.catch((error) => {
				alert.error("Unable to get stream log", error.response);
			});
		}, 30000);
	},
	beforeDestroy() {
		clearInterval(this.streamViewersUpdateInterval);
	}
};
</script>

<style lang="scss" scoped>
.session-list-item__meta {
	min-height: 437px;
	order: 0;
	overflow: hidden;
	.session-list-item__meta__stream_title {
		background-color: rgba(0, 0, 0, 0.12);
		height: 42px;
		padding: 8px 12px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.session-list-item__meta__header {
		padding: 12px;
		button {
			float: left;
			margin-left: -8px;
			&.active {
				/deep/ i {
					color: var(--ui-theme-secondary);
				}
			}
		}
		.session-list-item__meta__header__title {
			overflow: hidden;
			white-space: nowrap;
			.session-list-item__meta__header__title__name {
				line-height: 23px;
			}
			.session-list-item__meta__header__title__name,
			.session-list-item__meta__header__title__uri {
				overflow: hidden;
				text-overflow: ellipsis;
			}
		}
	}
	.session-list-item__meta__content {
		background-color: rgba(0, 0, 0, 0.12);
		height: 72px;
		button {
			margin-top: 8px;
			float: right;
		}
		.dynamic-duration {
			float: left;
			margin-top: 12px;
			margin-left: 8px;
		}
		.session-list-item__meta__content__connecting {
			margin-left: 8px;
			.ui-title {
				float: left;
				line-height: 72px;
			}
		}
	}
	.ui-progress-bar {
		margin-bottom: -2px;
	}
	.session-list-item__meta__footer {
		padding: 8px 12px;
		.ui-subheading {
			margin-top: 8px;
		}
		.session-list-item__meta__footer__stream_input,
		.session-list-item__meta__footer__stream_output {
			display: grid;
			grid-template-columns: repeat(2, 1fr);
			>div {
				text-align: left;
			}
			>div:nth-child(2n) {
				text-align: right;
			}
		}
		.session-list-item__meta__footer__link {
			margin-top: 16px;
		}
	}
}
// 3 columns
@media (min-width: 1200px) {
	.session-list-item__meta {
		order: 1;
		min-width: 280px;
		max-width: 340px;
	}
}

// IE11 specific, using flex due to poor grid support
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
	.session-list-item__meta {
		.session-list-item__meta__footer {
			.session-list-item__meta__footer__stream_input,
			.session-list-item__meta__footer__stream_output {
				display: flex;
				flex-wrap: wrap;
				justify-content: space-between;
				>div {
					width: 50%;
				}
			}
		}
	}
}
</style>
